/**
 * PlanNodeDetailPanel — bottom-docked, user-resizable inspector for a plan-graph node.
 *
 * Opened by single-clicking a node card in PlanGraphView. Rendered as an in-flow
 * flex-shrink-0 block BELOW the grid, so mounting it COMPRESSES the canvas
 * (push, not overlay — nothing is hidden). A grip handle on the top edge lets
 * the user drag-resize the drawer (double-click resets); the chosen height
 * persists for the session via a module-level variable.
 *
 * Layout: header row (phase chip · title · status pill · Open workspace ·
 * close) + a three-section body grid:
 *   1. WHAT THIS NODE DOES — the planner's full description (LogicNode.content)
 *   2. INPUTS — one row per incoming edge: source title + the edge's
 *      output_spec (the handoff contract) + the source's status dot
 *   3. OUTPUT — the node's deliverable + one row per outgoing edge
 *
 * Pure presentational — styling uses DESIGN.md tokens. PHASE_ACCENT /
 * STATUS_COLOR are imported from PlanGraphView so both surfaces share one
 * colour vocabulary (value imports are only read at render time, so the
 * module cycle with PlanGraphView is benign).
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import type { PlanNodeData, PlanEdgeData } from './PlanGraphView';
import { PHASE_ACCENT, STATUS_COLOR } from './PlanGraphView';
import { PlanNodeBriefBody, STATUS_LABELS, WORKSPACE_TYPE_LABELS } from './PlanNodeBrief';
import { useProjectStore } from '../../stores/projectStore';
import { getWorkspaceState } from '../../apiService';
import type { SealedRegulatoryScope } from '../../types/workspaceState';

// Drawer sizing — the default mirrors the previous fixed clamp; a user drag
// overrides it for the rest of the session (module-level so it survives
// close/reopen and node switches; intentionally NOT persisted to backend).
const DEFAULT_HEIGHT = 'clamp(240px, 38%, 340px)';
const MIN_HEIGHT_PX = 160;
const MAX_FRACTION = 0.75;
let sessionHeight: number | null = null;

interface PlanNodeDetailPanelProps {
  node: PlanNodeData;
  edges: PlanEdgeData[];
  nodeById: (nodeId: string) => PlanNodeData | undefined;
  onOpenWorkspace: (nodeId: string, workspaceId?: string | null) => void;
  onClose: () => void;
}

export default function PlanNodeDetailPanel({
  node,
  edges,
  nodeById,
  onOpenWorkspace,
  onClose,
}: PlanNodeDetailPanelProps) {
  // ---------------------------------------------------------------------------
  // Sealed regulatory scope (BAR-505) — lives on the CHILD research workspace's
  // /state, not the plan state this drawer is fed from. Store-first (the store
  // may already hold that workspace live), else a one-shot authed fetch.
  // ---------------------------------------------------------------------------
  const isResearch = node.workspace_type === 'research';
  const childWsId = node.workspace_id ?? null;
  const userId = useProjectStore((s) => s.activeProject?.user_id ?? null);
  const dictWorkspaceId = useProjectStore((s) => s.dictWorkspace?.workspace_id ?? null);
  const storeScope = useProjectStore((s) => s.dictWorkspace?.regulatory_scope ?? null);
  const [fetchedScope, setFetchedScope] = useState<SealedRegulatoryScope | null>(null);
  const storeIsChild = isResearch && !!childWsId && dictWorkspaceId === childWsId;

  // Reset synchronously when the node's workspace changes (React's
  // adjust-state-on-prop-change pattern). Effects run AFTER paint, so without
  // this the previous node's fetched scope would flash on the new node's
  // first frame if this component is ever rendered without a node key.
  const [scopeWsId, setScopeWsId] = useState<string | null>(childWsId);
  if (scopeWsId !== childWsId) {
    setScopeWsId(childWsId);
    setFetchedScope(null);
  }

  useEffect(() => {
    setFetchedScope(null);
    if (!isResearch || !childWsId || storeIsChild || !userId) return;
    let cancelled = false;
    getWorkspaceState(childWsId, userId)
      .then((res) => {
        if (!cancelled) setFetchedScope(res.state?.regulatory_scope ?? null);
      })
      .catch(() => {
        /* scope stays null — the section shows its empty state */
      });
    return () => { cancelled = true; };
  }, [isResearch, childWsId, storeIsChild, userId]);

  const regulatoryScope = storeIsChild ? storeScope : fetchedScope;

  // ---------------------------------------------------------------------------
  // Resizable drawer height (session-persisted)
  // ---------------------------------------------------------------------------
  const [height, setHeight] = useState<number | null>(sessionHeight);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: panel.getBoundingClientRect().height };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || !panel) return;
    // Cap at ~75% of the plan view (header + canvas + drawer flex column).
    const containerH = panel.parentElement?.clientHeight ?? window.innerHeight;
    const next = Math.round(
      Math.min(Math.max(drag.startH + (drag.startY - e.clientY), MIN_HEIGHT_PX), containerH * MAX_FRACTION),
    );
    sessionHeight = next;
    setHeight(next);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const resetHeight = () => {
    sessionHeight = null;
    setHeight(null);
  };

  // ---------------------------------------------------------------------------
  // Derived display data
  // ---------------------------------------------------------------------------
  const accent = PHASE_ACCENT[node.workspace_type || ''] || 'var(--fg-tertiary)';
  const statusColor = STATUS_COLOR[node.status] || 'var(--fg-tertiary)';
  const phaseLabel = node.workspace_type
    ? WORKSPACE_TYPE_LABELS[node.workspace_type] || node.workspace_type
    : null;

  return (
    <div
      ref={panelRef}
      className="relative flex-shrink-0 w-full bg-surface border-t border-border-default flex flex-col"
      style={{
        height: height != null
          ? `clamp(${MIN_HEIGHT_PX}px, ${height}px, ${MAX_FRACTION * 100}%)`
          : DEFAULT_HEIGHT,
      }}
    >
      {/* Resize handle — straddles the top border; drag to resize, double-click resets */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize details panel"
        title="Drag to resize · double-click to reset"
        className="absolute -top-1 left-0 right-0 h-2.5 cursor-ns-resize flex items-center justify-center group z-20"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={resetHeight}
      >
        <span className="w-8 h-1 rounded-full bg-border-strong group-hover:bg-fg-tertiary transition-colors" />
      </div>

      {/* Header — phase chip · title · status pill · actions */}
      <div className="flex items-center gap-2.5 px-4 h-10 border-b border-border-default flex-shrink-0 min-w-0">
        {phaseLabel && (
          <span
            className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] text-fg-secondary flex-shrink-0"
            style={{
              background: `color-mix(in srgb, ${accent} 14%, var(--surface))`,
              border: `1px solid color-mix(in srgb, ${accent} 40%, var(--border))`,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
            {phaseLabel}
          </span>
        )}
        <h3 className="text-[13px] font-semibold text-fg truncate min-w-0">{node.text}</h3>
        <span className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full bg-bg-warm border border-border-default text-[11px] font-medium text-fg-secondary flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
          {STATUS_LABELS[node.status] || node.status}
        </span>
        <div className="flex-1" />
        {node.workspace_id && (
          <button
            onClick={() => onOpenWorkspace(node.node_id, node.workspace_id)}
            className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-md bg-primary text-on-primary text-[12px] font-medium hover:bg-primary-hover flex-shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open workspace
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1 rounded text-fg-tertiary hover:text-fg-secondary hover:bg-bg-warm flex-shrink-0"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body — the shared what/inputs/output grid (PlanNodeBrief). The
          workspace-level WorkspaceBrief renders the SAME body, so the node
          presents identically in the plan view and inside its workspace. */}
      <PlanNodeBriefBody
        node={node}
        edges={edges}
        nodeById={nodeById}
        regulatoryScope={regulatoryScope}
      />
    </div>
  );
}
